import {Alert, Anchor, Breadcrumbs, Stack, Text, Title} from "@mantine/core";
import {useQuery} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {useMemo} from "react";
import {Link, useParams} from "react-router";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import Page from "../../components/Page/Page.tsx";
import api from "../../utils/api.ts";
import Prospector from "./Prospector.tsx";
import {AsteroidFieldSummary} from "./types.ts";

function formatUpdated(updatedAt: number) {
    return DateTime.fromSeconds(updatedAt).toFormat("yyyy-LL-dd HH:mm");
}

function displayName(field: AsteroidFieldSummary) {
    if (field.fieldGalX === null || field.fieldGalY === null) {
        return field.name;
    }

    return `${field.name} (${field.fieldGalX}, ${field.fieldGalY})`;
}

function AsteroidFieldDetailPage() {
    const {id} = useParams<{id?: string}>();

    const fieldsQuery = useQuery<AsteroidFieldSummary[]>({
        queryKey: ["asteroid-fields"],
        queryFn: async () => {
            const response = await api.get("/asteroid-fields");
            return response.data.fields as AsteroidFieldSummary[];
        },
    });

    const fields = fieldsQuery.data ?? [];
    const selected = useMemo(() => fields.find((field) => field.id === id) ?? null, [fields, id]);

    if (!id) {
        return (
            <Page>
                <Alert color="red" title="Missing field ID">No asteroid field was selected.</Alert>
            </Page>
        );
    }

    if (fieldsQuery.isPending) {
        return <LoaderScreen message="Loading asteroid field"/>;
    }

    return (
        <Page full>
            <title>{selected ? `${selected.name} Asteroid Field` : "Asteroid Field"}</title>

            <Stack gap="md">
                <div>
                    <Breadcrumbs>
                        <Anchor component={Link} to="/asteroid-fields">Asteroid Fields</Anchor>
                        <Anchor component={Link} to={`/asteroid-fields/${id}`}>{selected ? displayName(selected) : id}</Anchor>
                    </Breadcrumbs>

                    <Title order={1}>{selected ? displayName(selected) : "Asteroid Field"}</Title>
                    {selected ? (
                        <Text c="dimmed" size="sm">
                            Updated {formatUpdated(selected.updatedAt)}. {selected.depositCount} deposits, {selected.eventCount} events.
                        </Text>
                    ) : (
                        <Text c="dimmed" size="sm">Field summary unavailable.</Text>
                    )}
                </div>

                <Prospector fieldId={id} onDataChange={() => fieldsQuery.refetch()}/>
            </Stack>
        </Page>
    );
}

export default withRouteErrorBoundary(AsteroidFieldDetailPage);
